export type StatusRequestKind
  = | 'active'
    | 'background'
    | 'compact_fallback'
    | 'retry'
    | 'safety_poll'
    | 'sse'
    | 'terminal'

export type StatusTransport = 'compact' | 'legacy'

export type StatusTelemetryVisibility = 'hidden' | 'visible'

export type StatusTelemetryLifecycleEvent
  = | 'visibility_hidden'
    | 'visibility_visible'
    | 'freeze'
    | 'resume'
    | 'pagehide'
    | 'pageshow'
    | 'offline'
    | 'online'

export interface StatusRequestTelemetrySample {
  applyMs: number | null
  clientObservedAtMs: number
  completeMs: number
  dataAgeMs: number | null
  firstByteMs: number | null
  invalidationToRefetchMs: number | null
  kind: StatusRequestKind
  online: boolean
  outcome: 'error' | 'ok'
  pollGapMs: number | null
  sampleType: 'request'
  sequence: number
  statusVersion: string | null
  transport: StatusTransport
  visibility: StatusTelemetryVisibility
}

export interface StatusLifecycleTelemetrySample {
  applyMs?: never
  clientObservedAtMs: number
  completeMs?: never
  dataAgeMs?: never
  firstByteMs?: never
  invalidationToRefetchMs?: never
  kind?: never
  lifecycle: StatusTelemetryLifecycleEvent
  online: boolean
  outcome?: never
  persisted: boolean | null
  pollGapMs?: never
  sampleType: 'lifecycle'
  sequence: number
  statusVersion?: never
  transport?: never
  visibility: StatusTelemetryVisibility
}

export type StatusTelemetrySample
  = | StatusLifecycleTelemetrySample
    | StatusRequestTelemetrySample

export interface StatusTelemetryOptions {
  clientNow?: () => number
  flush: (samples: StatusTelemetrySample[]) => Promise<unknown>
  flushAt?: number
  maxBuffer?: number
  now?: () => number
  online?: () => boolean
  random?: () => number
  sampleRate?: number
  visibility?: () => StatusTelemetryVisibility
}

export interface StatusRequestRecorder {
  applied: () => void
  complete: (result: {
    dataAgeMs?: number | null
    outcome: 'error' | 'ok'
    statusVersion?: string | null
    transport?: StatusTransport
  }) => void
  firstByte: () => void
}

const NOOP_REQUEST_RECORDER: StatusRequestRecorder = {
  applied: () => {},
  complete: () => {},
  firstByte: () => {},
}

const MAX_SAMPLE_MS = 600_000
const MAX_POLL_GAP_MS = 24 * 60 * 60 * 1000
const MAX_BUFFERED_SAMPLES = 50
const MAX_SAFE_SEQUENCE = Number.MAX_SAFE_INTEGER

function clampMs(value: number | null): number | null {
  if (value === null)
    return null
  if (!Number.isFinite(value))
    return null
  return Math.min(MAX_SAMPLE_MS, Math.max(0, Math.round(value)))
}

function clampPollGapMs(value: number | null): number | null {
  if (value === null)
    return null
  if (!Number.isFinite(value))
    return null
  return Math.min(MAX_POLL_GAP_MS, Math.max(0, Math.round(value)))
}

function defaultNow(): number {
  try {
    return typeof performance === 'undefined' ? Date.now() : performance.now()
  }
  catch {
    try {
      return Date.now()
    }
    catch {
      return 0
    }
  }
}

function defaultClientNow(): number {
  try {
    return Date.now()
  }
  catch {
    return 0
  }
}

function defaultOnline(): boolean {
  try {
    if (
      typeof navigator === 'undefined'
      || typeof navigator.onLine !== 'boolean'
    ) {
      return true
    }
    return navigator.onLine
  }
  catch {
    return true
  }
}

function defaultVisibility(): StatusTelemetryVisibility {
  try {
    if (typeof document === 'undefined')
      return 'visible'
    return document.visibilityState === 'visible' ? 'visible' : 'hidden'
  }
  catch {
    return 'visible'
  }
}

function clampClientObservedAtMs(value: number): number {
  if (!Number.isFinite(value))
    return 0
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.round(value)))
}

function positiveInteger(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value))
    return fallback
  return Math.max(1, Math.floor(Number(value)))
}

let fallbackSessionSequence = 0

export function createStatusTelemetrySessionId(): string {
  const bytes = new Uint8Array(16)
  try {
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
      globalThis.crypto.getRandomValues(bytes)
      return Array.from(bytes, byte =>
        byte.toString(16).padStart(2, '0')).join('')
    }
  }
  catch {}

  fallbackSessionSequence += 1
  let fallbackNow = 0
  try {
    fallbackNow = Date.now()
  }
  catch {}
  for (let index = 0; index < bytes.length; index += 1) {
    try {
      const draw = Math.random()
      bytes[index] = Number.isFinite(draw)
        ? Math.floor(draw * 256)
        : (fallbackNow + fallbackSessionSequence + index * 31) % 256
    }
    catch {
      bytes[index] = (fallbackNow + fallbackSessionSequence + index * 31) % 256
    }
  }
  bytes[0] = (bytes[0]! + fallbackSessionSequence) % 256
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(
    '',
  )
}

export class StatusTelemetryCollector {
  private buffer: StatusTelemetrySample[] = []
  private readonly clientNow: () => number
  private readonly flush: StatusTelemetryOptions['flush']
  private readonly flushAt: number
  private flushing = false
  private flushRequested = false
  private invalidatedAt: number | null = null
  private lastRequestObservedAtMs: number | null = null
  private readonly maxBuffer: number
  private readonly now: () => number
  private readonly online: () => boolean
  private readonly sampleRate: number
  private readonly sampledSession: boolean
  private sequence = 0
  private readonly visibility: () => StatusTelemetryVisibility

  constructor(options: StatusTelemetryOptions) {
    this.clientNow = options.clientNow ?? defaultClientNow
    this.flush = options.flush
    this.flushAt = positiveInteger(options.flushAt, 10)
    this.maxBuffer = Math.min(
      MAX_BUFFERED_SAMPLES,
      positiveInteger(options.maxBuffer, MAX_BUFFERED_SAMPLES),
    )
    this.now = options.now ?? defaultNow
    this.online = options.online ?? defaultOnline
    this.sampleRate = Number.isFinite(options.sampleRate)
      ? Math.min(100, Math.max(0, Number(options.sampleRate)))
      : 0
    this.sampledSession = this.decideSessionSampling(
      options.random ?? Math.random,
    )
    this.visibility = options.visibility ?? defaultVisibility
  }

  public markInvalidated(): void {
    if (!this.sampledSession)
      return
    try {
      this.invalidatedAt = this.now()
    }
    catch {}
  }

  public beginRequest(kind: StatusRequestKind): StatusRequestRecorder {
    if (!this.sampledSession)
      return NOOP_REQUEST_RECORDER

    let startedAt: number
    try {
      startedAt = this.now()
    }
    catch {
      return NOOP_REQUEST_RECORDER
    }

    const invalidationToRefetchMs
      = this.invalidatedAt === null ? null : startedAt - this.invalidatedAt
    this.invalidatedAt = null
    const context = this.clientContext()
    const observedKind
      = kind === 'active' && context.visibility === 'hidden'
        ? 'background'
        : kind
    const pollGapMs
      = this.lastRequestObservedAtMs === null
        ? null
        : context.clientObservedAtMs - this.lastRequestObservedAtMs
    this.lastRequestObservedAtMs = context.clientObservedAtMs
    const sequence = this.nextSequence()

    let firstByteMs: number | null = null
    let closed = false
    let buffered: StatusRequestTelemetrySample | null = null

    const elapsed = (): number => {
      try {
        return this.now() - startedAt
      }
      catch {
        return 0
      }
    }

    return {
      firstByte: () => {
        if (firstByteMs === null)
          firstByteMs = elapsed()
      },
      complete: (result) => {
        if (closed)
          return
        closed = true
        buffered = {
          applyMs: null,
          clientObservedAtMs: context.clientObservedAtMs,
          completeMs: clampMs(elapsed()) ?? 0,
          dataAgeMs: clampMs(result.dataAgeMs ?? null),
          firstByteMs: clampMs(firstByteMs),
          invalidationToRefetchMs: clampMs(invalidationToRefetchMs),
          kind: observedKind,
          online: context.online,
          outcome: result.outcome,
          pollGapMs: clampPollGapMs(pollGapMs),
          sampleType: 'request',
          sequence,
          statusVersion: result.statusVersion ?? null,
          transport: result.transport ?? 'legacy',
          visibility: context.visibility,
        }
        this.push(buffered)
      },
      applied: () => {
        if (buffered !== null && buffered.applyMs === null)
          buffered.applyMs = clampMs(elapsed())
      },
    }
  }

  public recordLifecycle(
    lifecycle: StatusTelemetryLifecycleEvent,
    persisted = false,
  ): void {
    if (!this.sampledSession)
      return
    try {
      this.pushLifecycle(lifecycle, persisted, this.clientContext())
    }
    catch {}
  }

  public recordVisibilityChange(): void {
    if (!this.sampledSession)
      return
    try {
      const context = this.clientContext()
      this.pushLifecycle(
        context.visibility === 'hidden'
          ? 'visibility_hidden'
          : 'visibility_visible',
        false,
        context,
      )
    }
    catch {}
  }

  public isSampledSession(): boolean {
    return this.sampledSession
  }

  public async flushNow(): Promise<void> {
    if (this.flushing) {
      this.flushRequested = true
      return
    }
    if (this.buffer.length === 0 || !this.readOnline())
      return
    this.flushing = true
    try {
      do {
        this.flushRequested = false
        const batch = this.buffer
        this.buffer = []
        try {
          await this.flush(batch)
        }
        catch {}
      } while (
        this.flushRequested
        && this.buffer.length > 0
        && this.readOnline()
      )
    }
    finally {
      this.flushing = false
    }
  }

  public pending(): readonly StatusTelemetrySample[] {
    return this.buffer
  }

  private push(sample: StatusTelemetrySample): void {
    try {
      this.buffer.push(sample)
      if (this.flushing)
        this.flushRequested = true
      if (this.buffer.length > this.maxBuffer)
        this.buffer.splice(0, this.buffer.length - this.maxBuffer)
      if (this.buffer.length >= this.flushAt && this.readOnline())
        void this.flushNow()
    }
    catch {}
  }

  private clientContext(): {
    clientObservedAtMs: number
    online: boolean
    visibility: StatusTelemetryVisibility
  } {
    let observedAt = 0
    try {
      observedAt = clampClientObservedAtMs(this.clientNow())
    }
    catch {}
    return {
      clientObservedAtMs: observedAt,
      online: this.readOnline(),
      visibility: this.readVisibility(),
    }
  }

  private decideSessionSampling(random: () => number): boolean {
    if (this.sampleRate <= 0)
      return false
    if (this.sampleRate >= 100)
      return true
    try {
      const draw = random()
      return Number.isFinite(draw) && draw * 100 < this.sampleRate
    }
    catch {
      return false
    }
  }

  private nextSequence(): number {
    this.sequence = Math.min(MAX_SAFE_SEQUENCE, this.sequence + 1)
    return this.sequence
  }

  private readOnline(): boolean {
    try {
      return Boolean(this.online())
    }
    catch {
      return true
    }
  }

  private readVisibility(): StatusTelemetryVisibility {
    try {
      return this.visibility() === 'hidden' ? 'hidden' : 'visible'
    }
    catch {
      return 'visible'
    }
  }

  private pushLifecycle(
    lifecycle: StatusTelemetryLifecycleEvent,
    persisted: boolean,
    context: {
      clientObservedAtMs: number
      online: boolean
      visibility: StatusTelemetryVisibility
    },
  ): void {
    const isPageTransition
      = lifecycle === 'pagehide' || lifecycle === 'pageshow'
    this.push({
      ...context,
      lifecycle,
      persisted: isPageTransition ? Boolean(persisted) : null,
      sampleType: 'lifecycle',
      sequence: this.nextSequence(),
    })
  }
}

export interface StatusTelemetryEventTarget {
  addEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) => void
  removeEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ) => void
}

export interface StatusTelemetryLifecycleTargets {
  document?: StatusTelemetryEventTarget
  window?: StatusTelemetryEventTarget
}

export function attachStatusTelemetryLifecycle(
  collector: StatusTelemetryCollector,
  targets: StatusTelemetryLifecycleTargets = {},
): () => void {
  if (!collector.isSampledSession())
    return () => {}

  const documentTarget = targets.document
    ?? (typeof document === 'undefined' ? undefined : document)
  const windowTarget = targets.window
    ?? (typeof window === 'undefined' ? undefined : window)
  const cleanups: Array<() => void> = []

  const subscribe = (
    target: StatusTelemetryEventTarget | undefined,
    type: string,
    listener: EventListener,
  ) => {
    if (!target)
      return
    try {
      target.addEventListener(type, listener)
      cleanups.push(() => {
        try {
          target.removeEventListener(type, listener)
        }
        catch {}
      })
    }
    catch {}
  }

  subscribe(documentTarget, 'visibilitychange', () => {
    collector.recordVisibilityChange()
  })
  subscribe(documentTarget, 'freeze', () => {
    collector.recordLifecycle('freeze')
    void collector.flushNow()
  })
  subscribe(documentTarget, 'resume', () => {
    collector.recordLifecycle('resume')
  })
  subscribe(windowTarget, 'pagehide', (event) => {
    collector.recordLifecycle(
      'pagehide',
      Boolean((event as PageTransitionEvent).persisted),
    )
    void collector.flushNow()
  })
  subscribe(windowTarget, 'pageshow', (event) => {
    collector.recordLifecycle(
      'pageshow',
      Boolean((event as PageTransitionEvent).persisted),
    )
  })
  subscribe(windowTarget, 'offline', () => {
    collector.recordLifecycle('offline')
  })
  subscribe(windowTarget, 'online', () => {
    collector.recordLifecycle('online')
    void collector.flushNow()
  })

  let cleaned = false
  return () => {
    if (cleaned)
      return
    cleaned = true
    for (const cleanup of cleanups) cleanup()
  }
}

export function resolveStatusTelemetrySampleRate(
  env: Record<string, unknown> = import.meta.env as Record<string, unknown>,
): number {
  try {
    const raw = env.VITE_STATUS_TELEMETRY_SAMPLE_RATE
    if (typeof raw !== 'string' || raw.trim() === '')
      return 0
    const parsed = Number(raw)
    if (!Number.isFinite(parsed))
      return 0
    return Math.min(100, Math.max(0, parsed))
  }
  catch {
    return 0
  }
}
